/**
 * 同 origin Public API client（ADR-0007）。
 *
 * - `baseUrl = '/api'`：前台与 API 同 origin（`snail.qtnu.edu.cn`），
 *   开发期由 Vite `server.proxy['/api']` 转发本地后端。不读
 *   `VITE_API_BASE_URL` / `VITE_CDN_BASE_URL`（同 origin 部署已废除）。
 * - 2xx → 解析 JSON + typed return；非 2xx → 解析 ProblemDetails 抛
 *   `ApiError`（含 stable `errorCode` + `traceId` + `status`）；
 *   网络/超时 → 抛 `NetworkError`。
 * - **Debug-First（AGENTS §六）**：不做静默重试、不返 fallback、不吞咽。
 *   失败显式抛 typed error，让 store 落 failed / ready+error。
 * - 公开与预览聚合合并于此：仅 URL path 与是否带 token query 不同，
 *   请求/响应解析/错误映射 100% 复用（预览端点 B14 返同结构含 Draft）。
 */
import type { AggregateView, VersionView } from '@/api/types';
import { transformAggregate } from '@/api/aggregate-transform';

const BASE_URL = '/api';
const PUBLIC_AGGREGATE_PATH = '/public/aggregate';
const PUBLIC_VERSION_PATH = '/public/version';
const PREVIEW_AGGREGATE_PATH = '/admin/preview/aggregate';
const PREVIEW_TOKEN_QUERY = 'token';

/** 请求硬超时（ms）。超时即 abort 抛 NetworkError，不静默等待。 */
const REQUEST_TIMEOUT_MS = 8_000;
/** ProblemDetails 缺省 errorCode（后端 `SystemErrorCodes.Unexpected`）。 */
const DEFAULT_ERROR_CODE = 'system.unexpected';
const DEFAULT_TRACE_ID = '';

/** RFC 7807 ProblemDetails + 后端扩展字段 `errorCode` / `traceId`。 */
export interface ProblemDetails {
  readonly title?: string;
  readonly status?: number;
  readonly type?: string;
  readonly detail?: string;
  readonly instance?: string;
  readonly errorCode?: string;
  readonly traceId?: string;
  readonly errors?: Readonly<Record<string, readonly string[]>>;
}

/** ApiError 构造入参 bag；位置参数 ≤3（AGENTS §五）。 */
export interface ApiErrorInit {
  readonly errorCode: string;
  readonly traceId: string;
  readonly status: number;
  readonly problemDetails: ProblemDetails;
  readonly message?: string;
}

/** HTTP 非 2xx 响应抛出。`errorCode` 是稳定契约，前台据此映射文案。 */
export class ApiError extends Error {
  readonly errorCode: string;
  readonly traceId: string;
  readonly status: number;
  readonly problemDetails: ProblemDetails;

  constructor(init: ApiErrorInit) {
    super(init.message ?? `API error ${init.status} [${init.errorCode}]`);
    this.name = 'ApiError';
    this.errorCode = init.errorCode;
    this.traceId = init.traceId;
    this.status = init.status;
    this.problemDetails = init.problemDetails;
  }
}

/** 网络层失败（fetch reject / 超时）抛出。无 HTTP 状态，status=0。 */
export class NetworkError extends Error {
  readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** `GET /api/public/aggregate` —— 首屏 payload（B13）。 */
export async function fetchAggregate(): Promise<AggregateView> {
  const data = await request<AggregateView>(`${BASE_URL}${PUBLIC_AGGREGATE_PATH}`);
  return transformAggregate(data);
}

/** `GET /api/public/version` —— 轻量版本校验（ADR-0003）。 */
export function fetchVersion(): Promise<VersionView> {
  return request<VersionView>(`${BASE_URL}${PUBLIC_VERSION_PATH}`);
}

/**
 * `GET /api/admin/preview/aggregate?token=` —— 预览聚合（B14）。
 * token 作 query param；结构同 `AggregateView` 但含 Draft + 全量 Material。
 * token 401/403/过期由 `request` 抛 `ApiError`，store 落 failed。
 */
export async function fetchPreviewAggregate(token: string): Promise<AggregateView> {
  const url = `${BASE_URL}${PREVIEW_AGGREGATE_PATH}?${PREVIEW_TOKEN_QUERY}=${encodeURIComponent(token)}`;
  const data = await request<AggregateView>(url);
  return transformAggregate(data);
}

/**
 * 统一 fetch 入口：超时 abort、2xx 解析、非 2xx 抛 ApiError、网络抛 NetworkError。
 * 不重试、不兜底。timeout 句柄在 finally 清理，避免 timer 泄漏影响测试时序。
 */
async function request<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, { signal: controller.signal });
  } catch (error) {
    throw toNetworkError(error);
  } finally {
    clearTimeout(timer);
  }
  if (response.ok) {
    return parseJson<T>(response);
  }
  return parseProblemAndThrow(response);
}

/** body 非 JSON 时退化为最小 ProblemDetails，仍抛 ApiError 暴露状态码。 */
async function parseProblemAndThrow(response: Response): Promise<never> {
  const fallback: ProblemDetails = { status: response.status };
  let problem: ProblemDetails = fallback;
  try {
    problem = (await parseJson<ProblemDetails>(response)) ?? fallback;
  } catch (error) {
    // Debug-First：JSON 解析失败的原因显式暴露，与 cache.ts clearAndLog 口径一致。
    // 仍抛 ApiError 携 status，调用方不丢错误信号；只是 problemDetails 退化。
    // eslint-disable-next-line no-console
    console.error('[api-client] non-JSON ProblemDetails body', error);
  }
  const errorCode = problem.errorCode ?? DEFAULT_ERROR_CODE;
  const traceId = problem.traceId ?? DEFAULT_TRACE_ID;
  throw new ApiError({ errorCode, traceId, status: response.status, problemDetails: problem });
}

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

/** fetch reject（TypeError "Failed to fetch"）或 abort（超时）→ NetworkError。 */
function toNetworkError(error: unknown): NetworkError {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new NetworkError('请求超时', error);
  }
  return new NetworkError('网络异常，请求未完成', error);
}
